import React, { useState, useEffect } from 'react';
import PlayerService from '../../services/PlayerService';
import NotificationManager from '../../utils/NotificationManager';
import accountStore from './AccountStore';
import './ReferralDashboard.css';

const ReferralDashboard = ({ onClose }) => {
  const [referralStats, setReferralStats] = useState({
    code: '',
    count: 0
  });
  const [referredUsers, setReferredUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const generateGeometricPattern = (code) => {
    if (!code) return null;
    
    // Convert code to numbers we can use for the pattern
    const numbers = code.split('').map(char => char.charCodeAt(0));
    const sum = numbers.reduce((a, b) => a + b, 0);
    
    // Generate unique pattern based on code
    const elements = [];
    const points = [];
    const baseRadius = 28;
    const rotation = (numbers[0] % 60) - 30; // -30 to +30 degrees

    // Create main triangle points
    for (let i = 0; i < 3; i++) {
      const angle = (i * 120 + rotation) * (Math.PI / 180);
      const radius = baseRadius + (numbers[i] % 8);
      points.push({
        x: 36 + radius * Math.cos(angle),
        y: 36 + radius * Math.sin(angle)
      });
    }

    // Create secondary points
    const secondaryPoints = [];
    for (let i = 0; i < 3; i++) {
      const angle = ((i * 120 + 60 + rotation) * Math.PI) / 180;
      const radius = baseRadius - 8 + (numbers[i + 3] % 6);
      secondaryPoints.push({
        x: 36 + radius * Math.cos(angle),
        y: 36 + radius * Math.sin(angle)
      });
    }

    // Create outer shape
    elements.push(
      <g key="outer-shape">
        {/* Main triangle */}
        <polygon
          points={points.map(p => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke="#33ff33"
          strokeWidth="2"
          opacity="0.8"
        />
        {/* Secondary triangle */}
        <polygon
          points={secondaryPoints.map(p => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke="#33ff33"
          strokeWidth="1.5"
          opacity="0.6"
        />
        {/* Connecting lines between triangles */}
        {points.map((point, i) => (
          <line
            key={`connect-${i}`}
            x1={point.x}
            y1={point.y}
            x2={secondaryPoints[i].x}
            y2={secondaryPoints[i].y}
            stroke="#33ff33"
            strokeWidth="1"
            opacity="0.4"
          />
        ))}
      </g>
    );

    // Create inner shapes
    const innerPoints = points.map(p => ({
      x: 36 + (p.x - 36) * 0.4,
      y: 36 + (p.y - 36) * 0.4
    }));

    elements.push(
      <g key="inner-shapes">
        <polygon
          points={innerPoints.map(p => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke="#33ff33"
          strokeWidth="1"
          opacity="0.5"
        />
        {innerPoints.map((point, i) => (
          <line
            key={`inner-line-${i}`}
            x1={point.x}
            y1={point.y}
            x2="36"
            y2="36"
            stroke="#33ff33"
            strokeWidth="1"
            opacity="0.3"
          />
        ))}
      </g>
    );

    // Add vertices with glowing effect
    [...points, ...secondaryPoints, ...innerPoints].forEach((point, i) => {
      elements.push(
        <g key={`vertex-${i}`}>
          <circle
            cx={point.x}
            cy={point.y}
            r="3"
            fill="none"
            stroke="#33ff33"
            strokeWidth="1"
            opacity="0.3"
            filter="url(#glow)"
          />
          <circle
            cx={point.x}
            cy={point.y}
            r="1.5"
            fill="#33ff33"
            filter="url(#glow)"
          />
        </g>
      );
    });

    // Add center point with enhanced glow effect
    elements.push(
      <g key="center-point">
        <circle
          cx="36"
          cy="36"
          r="5"
          fill="none"
          stroke="#33ff33"
          strokeWidth="1"
          opacity="0.2"
          filter="url(#glow)"
        />
        <circle
          cx="36"
          cy="36"
          r="3"
          fill="none"
          stroke="#33ff33"
          strokeWidth="1"
          opacity="0.4"
          filter="url(#glow)"
        />
        <circle
          cx="36"
          cy="36"
          r="2"
          fill="#33ff33"
          opacity="0.8"
          filter="url(#glow)"
        />
      </g>
    );

    return (
      <svg
        width="72"
        height="72"
        viewBox="0 0 72 72"
        style={{ display: 'block', margin: '4px auto' }}
      >
        <defs>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0
                      0 1 0 0 1
                      0 0 0 0 0
                      0 0 0 0.7 0"
            />
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <g transform="rotate(30, 36, 36)">
          {elements}
        </g>
      </svg>
    );
  };

  useEffect(() => {
    console.log('ReferralDashboard mounted');
    initializeReferralSystem();
  }, []);

  // Fetch referred users' data
  const fetchReferredUsersData = async (userIds) => {
    if (!userIds || userIds.length === 0) return [];
    
    try {
      const usersData = await Promise.all(
        userIds.map(async (userId) => {
          const userData = await PlayerService.getPlayerData(userId);
          return {
            id: userId,
            username: userData.data?.discordUsername || 'Unknown User',
            joinedAt: userData.data?.referredAt || 'Unknown Date'
          };
        })
      );
      return usersData;
    } catch (error) {
      console.error('Error fetching referred users data:', error);
      return [];
    }
  };

  const initializeReferralSystem = async () => {
    console.log('Starting referral system initialization...');
    try {
      // Get current profile from AccountStore
      const currentProfile = accountStore.getProfile();
      console.log('Got profile from AccountStore:', currentProfile);

      if (!currentProfile?.data) {
        console.log('No profile data found');
        NotificationManager.notify('Please log in to view referral stats');
        setLoading(false);
        return;
      }

      // Check if referral object exists
      if (!currentProfile.data.referral) {
        console.log('No referral object found - this should not happen for new users!');
        console.log('User may have been created before referral system update.');

        // Generate referral code for legacy users
        const code = await PlayerService.generateReferralCode(currentProfile.data.discordUsername);
        console.log('Generated referral code for legacy user:', code);

        try {
          // Create initial referral object
          const success = await PlayerService.updatePlayerData(currentProfile.playerId, {
            ...currentProfile.data,
            referral: {
              code,
              count: 0,
              referredUsers: [],
              totalEarned: 0,
              lastUpdated: new Date().toISOString()
            },
            lastUpdated: new Date().toISOString()
          }, { increment: false });

          if (success) {
            console.log('Refreshing AccountStore...');
            // Refresh profile in AccountStore to get updated data
            await accountStore.refreshProfile();
            const updatedProfile = accountStore.getProfile();
            console.log('Got updated profile:', updatedProfile?.data);

            // Update local state with initial data
            setReferralStats({
              code: updatedProfile.data.referral.code,
              count: 0
            });
          } else {
            console.log('Failed to create referral object');
            NotificationManager.notify('Failed to create referral data');
          }
        } catch (error) {
          console.error('Error creating referral object:', error);
          NotificationManager.notify('Error creating referral data');
          setLoading(false);
          return;
        }
      } else {
        console.log('Using existing referral data:', currentProfile.data.referral);
        // Use existing referral data
        const referral = currentProfile.data.referral;
        setReferralStats({
          code: referral.code,
          count: referral.count || 0
        });

        // Fetch data for referred users if any exist
        if (referral.referredUsers?.length > 0) {
          console.log('Fetching data for referred users:', referral.referredUsers);
          const usersData = await fetchReferredUsersData(referral.referredUsers);
          setReferredUsers(usersData);
        }
      }
      console.log('Initialization complete');
    } catch (error) {
      console.error('Error initializing referral system:', error);
      NotificationManager.notify('Failed to initialize referral system');
      // Set empty stats on error
      setReferralStats({
        code: '',
        count: 0
      });
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    const referralLink = `${window.location.origin}?ref=${referralStats.code}`;
    try {
      await navigator.clipboard.writeText(referralLink);
      NotificationManager.notify('Referral link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      NotificationManager.notify('Failed to copy referral link');
    }
  };


  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        fontFamily: 'IBM BIOS, monospace'
      }}>
        <div style={{
          color: '#33ff33',
          textShadow: '0 0 8px #33ff33',
          animation: 'dataFlicker 1s infinite',
          fontSize: '18px',
          letterSpacing: '2px',
          padding: '40px'
        }}>
          LOADING REFERRAL DATA...
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          zIndex: 9998,
          animation: 'fadeIn 0.2s ease-out'
        }}
      />

      {/* Referral Dashboard Panel */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        background: 'rgba(0, 0, 0, 0.95)',
        border: '2px solid #33ff33',
        boxShadow: '0 0 30px rgba(51, 255, 51, 0.3)',
        zIndex: 9999,
        color: '#33ff33',
        fontFamily: 'IBM BIOS, monospace',
        animation: 'pulse 2s infinite ease-in-out',
        backgroundImage: `
          linear-gradient(0deg, rgba(51, 255, 51, 0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(51, 255, 51, 0.05) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        padding: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '10px',
          borderBottom: '1px solid rgba(51, 255, 51, 0.3)'
        }}>
          <h2 style={{ margin: 0 }}>Referral Dashboard</h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(51, 255, 51, 0.1)',
              border: '1px solid #33ff33',
              color: '#33ff33',
              padding: '8px 16px',
              cursor: 'pointer',
              fontFamily: 'IBM BIOS, monospace',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={e => {
              e.target.style.background = 'rgba(51, 255, 51, 0.2)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.target.style.background = 'rgba(51, 255, 51, 0.1)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
            CLOSE
          </button>
        </div>

        <div className="quantum-id-card">
          <div className="id-card-header">
            <div className="agent-profile">
              <div className="agent-pattern">
                {generateGeometricPattern(referralStats.code)}
                <div className="pattern-overlay"></div>
              </div>
              <div className="agent-status">
                <span className="status-dot"></span>
                ACTIVE
              </div>
            </div>
            <div className="title-container">
              <div className="id-card-title">QUANTUM RECRUITMENT PROTOCOL</div>
              <div className="id-card-subtitle">AUTHORIZED AGENT LINK</div>
            </div>
          </div>
          <div className="id-card-content">
            <div className="id-card-field">
              <span className="field-label">AGENT ID:</span>
              <span className="field-value">{referralStats.code}</span>
            </div>
            <div className="id-card-field">
              <span className="field-label">RECRUITMENT LINK:</span>
              <div className="quantum-link-container">
                <input
                  type="text"
                  value={`${window.location.origin}?ref=${referralStats.code}`}
                  readOnly
                  className="quantum-link-input"
                />
                <button onClick={copyReferralLink} className="quantum-copy-btn">
                  <span>COPY LINK</span>
                </button>
              </div>
            </div>
            <div className="id-card-footer">
              <span>TOTAL RECRUITS: {referralStats.count}</span>
              <div className="quantum-seal">QRP</div>
            </div>
          </div>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, marginTop: '20px' }}>

          {/* Referred Users List */}
          <div className="referred-users-section">
            <h3>Users You've Referred</h3>
            <div className="referred-users-list">
              {referredUsers.length > 0 ? (
                referredUsers.map((user) => (
                  <div key={user.id} className="referred-user-item">
                    <span className="username">{user.username}</span>
                    <span className="join-date">Joined {new Date(user.joinedAt).toLocaleDateString()}</span>
                  </div>
                ))
              ) : (
                <p>No users referred yet</p>
              )}
            </div>
          </div>
        </div>

        <style>
          {`
            @keyframes pulse {
              0% { box-shadow: 0 0 30px rgba(51, 255, 51, 0.3); }
              50% { box-shadow: 0 0 40px rgba(51, 255, 51, 0.4); }
              100% { box-shadow: 0 0 30px rgba(51, 255, 51, 0.3); }
            }
            @keyframes dataFlicker {
              0% { opacity: 1; }
              95% { opacity: 1; }
              96% { opacity: 0.8; }
              97% { opacity: 1; }
              98% { opacity: 0.9; }
              100% { opacity: 1; }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }

            ::-webkit-scrollbar {
              width: 8px;
            }

            ::-webkit-scrollbar-track {
              background: rgba(51, 255, 51, 0.05);
            }

            ::-webkit-scrollbar-thumb {
              background: rgba(51, 255, 51, 0.3);
              border-radius: 4px;
            }

            ::-webkit-scrollbar-thumb:hover {
              background: rgba(51, 255, 51, 0.5);
            }

            .quantum-id-card {
              background: rgba(0, 20, 0, 0.9);
              border: 2px solid #33ff33;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 24px;
              position: relative;
              overflow: hidden;
            }

            .quantum-id-card::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background:
                linear-gradient(45deg, transparent 48%, rgba(51, 255, 51, 0.1) 50%, transparent 52%),
                linear-gradient(-45deg, transparent 48%, rgba(51, 255, 51, 0.1) 50%, transparent 52%);
              background-size: 30px 30px;
              animation: scanline 3s linear infinite;
            }

            @keyframes scanline {
              0% {
                background-position: 0 0;
              }
              100% {
                background-position: 60px 60px;
              }
            }

            .id-card-header {
              display: flex;
              align-items: center;
              margin-bottom: 20px;
              position: relative;
              z-index: 1;
              gap: 20px;
            }

            .agent-profile {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 8px;
            }

            .agent-pattern {
              width: 80px;
              height: 80px;
              position: relative;
              border: 2px solid rgba(51, 255, 51, 0.5);
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 0 15px rgba(51, 255, 51, 0.2);
              background: rgba(0, 20, 0, 0.9);
              padding: 4px;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .pattern-overlay {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: radial-gradient(
                circle at 50% 50%,
                transparent 0%,
                rgba(51, 255, 51, 0.05) 50%,
                transparent 100%
              );
              animation: pulse 4s ease-in-out infinite;
              pointer-events: none;
            }

            .agent-status {
              display: flex;
              align-items: center;
              gap: 5px;
              font-size: 12px;
              color: rgba(51, 255, 51, 0.8);
            }

            .status-dot {
              width: 8px;
              height: 8px;
              background: #33ff33;
              border-radius: 50%;
              box-shadow: 0 0 10px #33ff33;
              animation: blink 2s infinite;
            }

            @keyframes blink {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }

            .title-container {
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: center;
            }

            .id-card-title {
              font-size: 18px;
              font-weight: bold;
              color: #33ff33;
              text-shadow: 0 0 10px rgba(51, 255, 51, 0.5);
              letter-spacing: 2px;
              margin-bottom: 8px;
            }

            .id-card-subtitle {
              font-size: 14px;
              color: rgba(51, 255, 51, 0.8);
              letter-spacing: 1px;
            }

            .id-card-content {
              position: relative;
              z-index: 1;
            }

            .id-card-field {
              margin: 15px 0;
            }

            .field-label {
              display: block;
              font-size: 12px;
              color: rgba(51, 255, 51, 0.7);
              margin-bottom: 5px;
            }

            .field-value {
              font-size: 16px;
              color: #33ff33;
              letter-spacing: 1px;
            }

            .quantum-link-container {
              display: flex;
              gap: 10px;
              align-items: center;
            }

            .quantum-link-input {
              flex: 1;
              background: rgba(0, 20, 0, 0.8);
              border: 1px solid rgba(51, 255, 51, 0.3);
              color: #33ff33;
              padding: 8px 12px;
              font-family: 'IBM BIOS', monospace;
              font-size: 14px;
              outline: none;
            }

            .quantum-copy-btn {
              background: rgba(51, 255, 51, 0.1);
              border: 1px solid #33ff33;
              color: #33ff33;
              padding: 8px 16px;
              cursor: pointer;
              font-family: 'IBM BIOS', monospace;
              font-size: 14px;
              transition: all 0.3s ease;
              position: relative;
              overflow: hidden;
            }

            .quantum-copy-btn:hover {
              background: rgba(51, 255, 51, 0.2);
              transform: translateY(-2px);
            }

            .quantum-copy-btn::before {
              content: '';
              position: absolute;
              top: -100%;
              left: 0;
              width: 100%;
              height: 100%;
              background: rgba(51, 255, 51, 0.2);
              transition: all 0.3s ease;
            }

            .quantum-copy-btn:hover::before {
              top: 0;
            }

            .quantum-copy-btn span {
              position: relative;
              z-index: 1;
            }

            .id-card-footer {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-top: 20px;
              padding-top: 15px;
              border-top: 1px solid rgba(51, 255, 51, 0.3);
              font-size: 14px;
              color: rgba(51, 255, 51, 0.8);
            }

            .quantum-seal {
              width: 40px;
              height: 40px;
              border: 2px solid #33ff33;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              color: #33ff33;
              text-shadow: 0 0 5px rgba(51, 255, 51, 0.5);
              animation: pulse 2s infinite ease-in-out;
            }

          `}
        </style>
      </div>

    </>
  );
};

export default ReferralDashboard;